import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { gerarTermoService } from '@/services/api';
import { Estagio, EstagioFilters } from '@/types';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

export const TermosPage: React.FC = () => {
  const [filters, setFilters] = useState<EstagioFilters>({
    page: 1,
    limit: 10,
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchTerm || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const { data: estagiosData, isLoading, error } = useQuery({
    queryKey: ['estagios', filters],
    queryFn: () => gerarTermoService.listEstagios(filters),
  });


  const clearFilters = () => {
    setFilters({ page: 1, limit: 10 });
    setSearchTerm('');
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleGerarTermo = async (estagioId: number) => {
    try {
      toast.loading('Gerando termo...', { id: 'gerar-termo' });
      const response = await gerarTermoService.gerarTermo(estagioId);
      
      // Criar link para download
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `termo-estagio-${estagioId}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Termo gerado com sucesso!', { id: 'gerar-termo' });
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      const errorMessage = axiosError?.response?.data?.error || 'Erro ao gerar termo';
      toast.error(errorMessage, { id: 'gerar-termo' });
    }
  };

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Erro ao carregar estágios</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Geração de Termos</h1>
        <p className="mt-1 text-sm text-gray-500">
          Gere termos de compromisso para os estágios cadastrados
        </p>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>Filtros</span>
            <span className="text-sm font-normal text-gray-500">
              ({estagiosData?.pagination.total || 0} estágios encontrados)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buscar
              </label>
              <input
                type="text"
                placeholder="Aluno, empresa, instituição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <Button
                variant="outline"
                onClick={clearFilters}
                className="px-6"
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Estágios List */}
      <Card>
        <CardHeader>
          <CardTitle>Estágios Disponíveis</CardTitle>
          <CardDescription>
            Selecione um estágio para gerar o termo de compromisso
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Carregando estágios...</p>
            </div>
          ) : estagiosData?.data.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhum estágio encontrado</p>
            </div>
          ) : (
            <div className="space-y-4">
              {estagiosData?.data.map((estagio: Estagio) => (
                <div
                  key={estagio.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">
                        {estagio.aluno?.nome || 'Aluno não encontrado'}
                      </h3>
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Empresa:</span> {estagio.empresa?.nome || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Instituição:</span> {estagio.instituicao?.nome || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Tipo:</span> {estagio.tipo}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Início:</span> {new Date(estagio.dataInicio).toLocaleDateString('pt-BR')}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Término:</span> {new Date(estagio.dataTermino).toLocaleDateString('pt-BR')}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Status:</span> {estagio.status}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Button 
                        onClick={() => handleGerarTermo(estagio.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                        Gerar Termo
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {estagiosData && estagiosData.pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={!estagiosData.pagination.hasPrev}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 text-sm text-gray-700">
                  Página {estagiosData.pagination.page} de {estagiosData.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={!estagiosData.pagination.hasNext}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
